import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { BRouterQuery, Track, TrackComment, TrackDataSet } from 'src/app/main/shared/DataTypes';
import { environment } from 'src/environments/environment';

export type TrackParameterName =
  | 'charging_station_data'
  | 'curve_data'
  | 'curve_radius_data_graz'
  | 'elevation_data'
  | 'gps_data'
  | 'cant_data'
  | 'hydrogen_refuel_station_data'
  | 'maximum_speed_data'
  | 'network_power_data'
  | 'station_data'
  | 'switches'
  | 'tunnel_factor_data';

export type TrackRenamePreview = {
  variantCount: number;
  affectedScheduleCount: number;
};

export type TrackRenameResponse = {
  changed: boolean;
  oldName: string;
  newName: string;
  oldVersion: string;
  newVersion: string;
  oldDescription: string;
  newDescription: string;
  tracksUpdated: number;
  schedulesJsonUpdated: number;
};

export type RelatedTrackVersion = {
  trackId: number;
  version: string;
};

export type ConnectedTrack = {
  scheduleLinkId: number;
  trackId: number;
  trackName: string;
  currentVersion: string;
  accessible: boolean;
  relatedVersions: RelatedTrackVersion[];
};

type BRouterTrackResponse = {
  brouterQuery?: BRouterQuery;
  parameters: { [key in TrackParameterName]?: TrackDataSet[] };
};

@Injectable({
  providedIn: 'root'
})
export class TrackService {
  private http = inject(HttpClient);

  readonly parameterNames: TrackParameterName[] = [
    'charging_station_data',
    'curve_data',
    'curve_radius_data_graz',
    'elevation_data',
    'gps_data',
    'cant_data',
    'hydrogen_refuel_station_data',
    'maximum_speed_data',
    'network_power_data',
    'station_data',
    'switches',
    'tunnel_factor_data'
  ];

  parameterNameMappings: {
    [K in TrackParameterName]: { [key: string]: { name: string; unit?: string } };
  } = {
    charging_station_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'charging_power_charging_station', unit: 'kW' }
    },
    curve_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'curve_radius', unit: 'm' }
    },
    elevation_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'height_above_sea_level', unit: 'm' },
      trackParameter2: { name: 'gradient', unit: '‰' }
    },
    gps_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'latitude', unit: '°' },
      trackParameter2: { name: 'longitude', unit: '°' }
    },
    hydrogen_refuel_station_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'hydrogen_quantity', unit: 'kg/s' }
    },
    maximum_speed_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'maximum_speed', unit: 'km/h' }
    },
    network_power_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'grid_power', unit: 'kW' },
      trackParameter2: { name: 'regenerative_power', unit: 'kW' }
    },
    station_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'station_name' }
    },
    tunnel_factor_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'tunnel_factor', unit: '' }
    },
    curve_radius_data_graz: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'curve_radius_graz', unit: '1/m' }
    },
    cant_data: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'cant', unit: '' }
    },
    switches: {
      position: { name: 'position', unit: 'm' },
      trackParameter1: { name: 'switch', unit: '' }
    }
  };

  getEmptyTrackParameters() {
    const parameters: { [key: string]: TrackDataSet[] } = {};
    this.parameterNames.forEach((parameterName: string) => {
      parameters[parameterName] = [];
    });
    return parameters;
  }

  /*
    Returns track data from the database for a specific track variant
  */
  getTrackDataFromDB(trackId: number): Observable<object> {
    const url = environment.apiURL + `/tracks/${trackId}`;
    return this.http.get(url).pipe(
      map((response: any) => {
        return this.getTrackParameters(response);
      })
    );
  }

  /*
    Returns new track with new track variant from map data
  */
  getTrackFromIonlats(trackEndpoints: any): Observable<Track> {
    const url = environment.apiURL + '/BRouter_interface/track';
    const data = {
      BRouter_track_endpoints: trackEndpoints
    };

    return this.http
      .post<BRouterTrackResponse>(url, data, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((response: BRouterTrackResponse) => {
          // Support legacy and current response keys to avoid losing query metadata.
          const brouterQuery = response.brouterQuery;
          const parameters = this.getTrackParameters(response.parameters);

          const stationData = parameters['station_data'];
          let name = 'New Track Variant'; // Default name

          if (stationData && Object.keys(stationData).length > 0) {
            const firstStation = stationData[0].trackParameter1;
            const lastStation = stationData[stationData.length - 1].trackParameter1;
            name = `${firstStation} - ${lastStation}`;
          }

          const newTrack: Track = {
            name: name,
            parameters: parameters,
            source: 'Map',
            deleted: false,
            tags: [],
            trackDataQuery: brouterQuery
          };
          return newTrack;
        })
      );
  }

  getTrackFromJSON(trackParameterData: any) {
    if (!this.isValidTrackJson(trackParameterData)) {
      throw new Error('Invalid track JSON schema');
    }

    const parameters = this.getTrackParameters(trackParameterData);
    const firstStation = parameters['station_data'][0]?.trackParameter1;
    const lastStation =
      parameters['station_data'][parameters['station_data'].length - 1]?.trackParameter1;
    const name = firstStation && lastStation ? `${firstStation} - ${lastStation}` : 'New Track';
    const newTrack: Track = {
      name,
      parameters: parameters,
      source: 'File',
      deleted: false,
      tags: []
    };
    return newTrack;
  }

  private isValidTrackJson(trackParameterData: unknown): boolean {
    if (!trackParameterData || typeof trackParameterData !== 'object') {
      return false;
    }

    const data = trackParameterData as Record<string, unknown>;

    // Exported track JSON must have at least one known parameter key.
    const hasKnownTrackKey = this.parameterNames.some(parameterName => parameterName in data);
    if (!hasKnownTrackKey) {
      return false;
    }

    // Schedule exports contain a top-level "schedule" object and should be rejected.
    if ('schedule' in data) {
      return false;
    }

    return true;
  }

  /*
    Returns parameter data for track variant from queried data
  */
  getTrackParameters(trackVariantData: any): { [key: string]: TrackDataSet[] } {
    const parameters: { [key: string]: TrackDataSet[] } = {};
    const parameterNames = this.parameterNames;

    parameterNames.forEach((parameterName: string) => {
      const parameterData: object[] = trackVariantData[parameterName];

      if (!parameterData) {
        parameters[parameterName] = [];
        return;
      }
      const newDataSet: TrackDataSet[] = [];

      // Create a TrackDataSet for each object in the array
      parameterData.forEach((parameterDataObject: any) => {
        const newParameterDataSet: TrackDataSet = {
          comments: [],
          warning: false,
          position: parameterDataObject.position ?? null,
          trackParameter1: parameterDataObject.trackParameter1 ?? null
        };
        // Dynamically add track parameters
        Object.keys(parameterDataObject).forEach((key: string) => {
          newParameterDataSet[key] = parameterDataObject[key];
        });

        newDataSet.push(newParameterDataSet); // Push the dataset into the array
      });

      // Store the TrackParameter in the parameters map
      parameters[parameterName] = newDataSet;
    });

    // Add default values for warning and comments
    Object.keys(parameters).forEach(key => {
      if (Array.isArray(parameters[key])) {
        parameters[key] = parameters[key].map(entry => {
          return {
            ...entry,
            warning: entry.warning ?? false,
            comments: entry.comments ?? ([] as TrackComment[])
          };
        });
      }
    });
    return parameters;
  }

  /*
  Returns default Track Variant with optional parameters to customize
*/
  getDefaultTrackVariant(customData?: any) {
    return {
      id: customData.id,
      track: customData.track ?? { id: 'None', name: 'None', variants: [] },
      variantName: customData.variantName ?? 'Original',
      version: customData.version ?? '1.0.0',
      parameters: customData.parameters ?? {},
      previousVersions: customData.previousVersions ?? [],
      description: customData.description ?? '',
      date: customData.date ?? new Date().toLocaleDateString('de-DE'),
      source: customData.source ?? 'Database'
    };
  }

  saveNewTrackToDB(track: Track, workspaceId: number): Observable<any> {
    const path = '/tracks';

    const payload = {
      track: {
        name: track.name,
        version: track.version,
        description: track.description,
        source: track.source,
        data: { ...track.parameters },
        trackDataQuery: track.trackDataQuery
      },
      workspaceId: workspaceId
    };
    return this.http.post(environment.apiURL + path, payload);
  }

  previewRenameTrack(oldName: string): Observable<TrackRenamePreview> {
    const params = new HttpParams().set('oldName', oldName);
    return this.http.get<TrackRenamePreview>(environment.apiURL + '/tracks/rename-preview', {
      params
    });
  }

  renameTrack(
    trackId: number,
    newName: string,
    newVersion: string,
    newDescription: string
  ): Observable<TrackRenameResponse> {
    return this.http.patch<TrackRenameResponse>(environment.apiURL + '/tracks/rename', {
      trackId: trackId,
      newName: newName,
      newVersion: newVersion,
      newDescription: newDescription
    });
  }

  getTrackFromDriveFile(file: File) {
    const path = environment.apiURL + '/drive-imports';
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(path, formData).pipe(
      map((response: any) => {
        const parameters = this.getTrackParameters(response);
        const firstStation = parameters['station_data'][0]?.trackParameter1;
        const lastStation =
          parameters['station_data'][parameters['station_data'].length - 1]?.trackParameter1;
        const name = firstStation && lastStation ? `${firstStation} - ${lastStation}` : file.name;
        const newTrack: Track = {
          name,
          parameters: parameters,
          source: 'File',
          deleted: false,
          tags: []
        };
        return newTrack;
      })
    );
  }

  getXNDSFileFromTrack(track: Track): Observable<any> {
    const url = environment.apiURL + '/xnds';
    const payload = {
      data: {
        ...track.parameters
      }
    };
    return this.http.post(url, payload);
  }

  getAllSchedulesContainingTracks(trackIds: number[]): Observable<any> {
    let params = new HttpParams();
    trackIds.forEach(trackId => {
      params = params.append('trackIds', trackId.toString());
    });

    return this.http.get(environment.apiURL + '/tracks/schedules', {
      params
    });
  }

  addCommentToTrack(
    trackId: number,
    parameterName: string,
    entryIndex: number,
    comment: string
  ): Observable<any> {
    const url = environment.apiURL + '/tracks/comments';
    const payload = {
      trackId: trackId,
      parameterName: parameterName,
      entryIndex: entryIndex,
      comment: comment
    };
    return this.http.post(url, payload);
  }

  deleteCommentFromTrack(
    trackId: number,
    parameterName: string,
    entryIndex: number,
    commentIndex: number
  ): Observable<any> {
    const url = environment.apiURL + '/tracks/comments';
    const payload = {
      trackId: trackId,
      parameterName: parameterName,
      entryIndex: entryIndex,
      commentIndex: commentIndex
    };
    return this.http.delete(url, { body: payload });
  }

  toggleRowWarningState(
    trackId: number,
    parameterName: string,
    entryIndex: number
  ): Observable<any> {
    const url = environment.apiURL + '/tracks/warnings/toggle';
    const payload = {
      trackId: trackId,
      parameterName: parameterName,
      entryIndex: entryIndex
    };
    return this.http.patch(url, payload);
  }

  getConnectedTracksFromScheduleId(scheduleId: number): Observable<ConnectedTrack[] | null> {
    const url = environment.apiURL + `/tracks/by-schedule-id/${scheduleId}`;
    return this.http.get<ConnectedTrack[] | null>(url);
  }

  changeLinkedTrackInSchedule(
    scheduleId: number,
    scheduleLinkId: number,
    newTrackId: number
  ): Observable<any> {
    const url = environment.apiURL + '/tracks/schedule-links';
    const payload = {
      scheduleId: scheduleId,
      scheduleLinkId: scheduleLinkId,
      newTrackId: newTrackId
    };
    return this.http.patch(url, payload);
  }
}
